/**
 * React Query key factory for the Social Media arche (W8 Vault-resident).
 *
 * Owned by the arche so the central `client/src/lib/query-keys.ts` does
 * not host per-arche namespaces. See
 * docs/audits/social-media-modularity-audit.md §10 PW-D.
 *
 * Every data-bearing key takes `holder` as its first segment so React
 * Query treats two Holders' data as fully independent caches. Switching
 * the Holder via `useHolderSwitcher` therefore invalidates the visible
 * data without manual `invalidateQueries` calls.
 *
 * The central file re-exports this under `queryKeys.socialMedia` so
 * existing consumers continue to work without change. New code inside
 * this arche should import `socialMediaQueryKeys` directly from here.
 */
export const socialMediaQueryKeys = {
  all: ['social-media'] as const,

  // Accounts
  accounts: (holder: string) => [...socialMediaQueryKeys.all, 'accounts', holder] as const,

  // Posts
  posts: (holder: string) => [...socialMediaQueryKeys.all, 'posts', holder] as const,
  /**
   * A specific paginated/filtered posts list view. Keyed off the `posts(holder)`
   * prefix so a mutation can `invalidateQueries({ queryKey: posts(holder) })` and
   * drop every status/search/page variant at once.
   */
  postsList: (
    holder: string,
    params: { status?: string; search?: string; page: number; limit: number },
  ) => [...socialMediaQueryKeys.posts(holder), 'list', params] as const,
  post: (holder: string, postId: string) =>
    [...socialMediaQueryKeys.posts(holder), postId] as const,
  postLog: (holder: string, postId: string) =>
    [...socialMediaQueryKeys.post(holder, postId), 'log'] as const,
  /** Posts visible in a calendar range — keyed by Holder + ISO from/to. */
  calendarPosts: (holder: string, from: string, to: string) =>
    [...socialMediaQueryKeys.all, 'calendar', holder, from, to] as const,

  // Brand settings
  brandSettings: (holder: string) =>
    [...socialMediaQueryKeys.all, 'brand-settings', holder] as const,

  // Inspiration
  inspiration: (holder: string) => [...socialMediaQueryKeys.all, 'inspiration', holder] as const,
  competitors: (holder: string) => [...socialMediaQueryKeys.all, 'competitors', holder] as const,
  rssFeeds: (holder: string) => [...socialMediaQueryKeys.all, 'rss-feeds', holder] as const,

  // Stats + analytics
  stats: (holder: string, month?: string) =>
    [...socialMediaQueryKeys.all, 'stats', holder, month ?? null] as const,
  analytics: (holder: string, range: string, platform?: string) =>
    [...socialMediaQueryKeys.all, 'analytics', holder, range, platform ?? null] as const,
};
